import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import axios from "axios";
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  Building2, 
  ShoppingCart, 
  Car, 
  GraduationCap,
  Wallet,
  CheckCircle2,
  PiggyBank
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEBT_TYPE_OPTIONS = [
  { value: "credit_card", label: "Credit Card", icon: CreditCard },
  { value: "personal_loan", label: "Personal Loan", icon: Wallet },
  { value: "overdraft", label: "Overdraft", icon: Building2 },
  { value: "store_card", label: "Store Card", icon: ShoppingCart },
  { value: "car_finance", label: "Car Finance", icon: Car },
  { value: "student_loan", label: "Student Loan", icon: GraduationCap },
  { value: "payday_loan", label: "Payday Loan", icon: Wallet },
  { value: "catalogue", label: "Catalogue Debt", icon: ShoppingCart },
  { value: "other", label: "Other", icon: CreditCard }
];

const DebtsPage = () => {
  const { token } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCreditor, setNewCreditor] = useState({
    name: "",
    debt_type: "",
    original_amount: "",
    current_balance: "",
    account_ref: "",
    included_in_iva: true
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/cases/my-case`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseData(response.data);
      setCreditors(response.data.creditors || []);
    } catch (error) {
      console.error("Case error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCreditor = () => {
    if (!newCreditor.name || !newCreditor.debt_type || !newCreditor.current_balance) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setCreditors([...creditors, {
      ...newCreditor,
      original_amount: parseFloat(newCreditor.original_amount) || parseFloat(newCreditor.current_balance),
      current_balance: parseFloat(newCreditor.current_balance)
    }]);
    
    setNewCreditor({
      name: "",
      debt_type: "",
      original_amount: "",
      current_balance: "",
      account_ref: "",
      included_in_iva: true
    });
    setShowAddForm(false);
    toast.success("Creditor added");
  };

  const handleRemoveCreditor = (index) => {
    setCreditors(creditors.filter((_, i) => i !== index));
    toast.success("Creditor removed");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cases/creditors`, creditors, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Creditors updated successfully");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const totalDebt = creditors.reduce((sum, c) => sum + (c.current_balance || 0), 0);
  const includedDebt = creditors.filter(c => c.included_in_iva).reduce((sum, c) => sum + (c.current_balance || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!caseData) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-600">No active case found. Please start an application first.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="debts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] mb-2">My Debts</h1>
            <p className="text-slate-600">Manage and track your creditors</p>
          </div>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-[#10B981] hover:bg-[#059669]"
            data-testid="add-creditor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Creditor
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-slate-600" />
              </div>
              <span className="text-sm text-slate-500">Total Creditors</span>
            </div>
            <p className="font-numbers text-3xl font-bold text-[#0F172A]">
              {creditors.length}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-red-600" />
              </div>
              <span className="text-sm text-slate-500">Total Debt</span>
            </div>
            <p className="font-numbers text-3xl font-bold text-[#0F172A]">
              {formatCurrency(totalDebt)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <PiggyBank className="w-5 h-5 text-[#10B981]" />
              </div>
              <span className="text-sm text-slate-500">Included in IVA</span>
            </div>
            <p className="font-numbers text-3xl font-bold text-[#10B981]">
              {formatCurrency(includedDebt)}
            </p>
          </motion.div>
        </div>

        {/* Add Creditor Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-[#10B981] shadow-lg"
          >
            <h3 className="font-semibold text-[#0F172A] mb-4">Add New Creditor</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Creditor Name *</label>
                <Input
                  placeholder="e.g., Barclays, HSBC"
                  value={newCreditor.name}
                  onChange={(e) => setNewCreditor({ ...newCreditor, name: e.target.value })}
                  data-testid="creditor-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Debt Type *</label>
                <Select
                  value={newCreditor.debt_type}
                  onValueChange={(value) => setNewCreditor({ ...newCreditor, debt_type: value })}
                >
                  <SelectTrigger data-testid="debt-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEBT_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Current Balance *</label>
                <Input
                  type="number"
                  placeholder="£0"
                  value={newCreditor.current_balance}
                  onChange={(e) => setNewCreditor({ ...newCreditor, current_balance: e.target.value })}
                  data-testid="current-balance-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Original Amount</label>
                <Input
                  type="number"
                  placeholder="£0"
                  value={newCreditor.original_amount}
                  onChange={(e) => setNewCreditor({ ...newCreditor, original_amount: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Account Reference</label>
                <Input
                  placeholder="Optional"
                  value={newCreditor.account_ref}
                  onChange={(e) => setNewCreditor({ ...newCreditor, account_ref: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddCreditor} className="bg-[#10B981] hover:bg-[#059669]" data-testid="confirm-add-btn">
                Add Creditor
              </Button>
            </div>
          </motion.div>
        )}

        {/* Creditors List */}
        <div className="space-y-4">
          {creditors.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 border border-slate-200 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 mb-4">No creditors added yet</p>
              <Button onClick={() => setShowAddForm(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Creditor
              </Button>
            </div>
          ) : (
            creditors.map((creditor, index) => {
              const typeInfo = DEBT_TYPE_OPTIONS.find(t => t.value === creditor.debt_type) || DEBT_TYPE_OPTIONS[8];
              const TypeIcon = typeInfo.icon;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-slate-300 transition-colors"
                  data-testid={`creditor-${index}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                        <TypeIcon className="w-6 h-6 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0F172A]">{creditor.name}</h3>
                        <p className="text-sm text-slate-500">{typeInfo.label}</p>
                        {creditor.account_ref && (
                          <p className="text-xs text-slate-400">Ref: {creditor.account_ref}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Balance</p>
                        <p className="font-numbers text-xl font-bold text-[#0F172A]">
                          {formatCurrency(creditor.current_balance)}
                        </p>
                      </div>
                      
                      {creditor.included_in_iva && (
                        <div className="flex items-center gap-1 text-[#10B981]">
                          <CheckCircle2 className="w-5 h-5" />
                          <span className="text-sm font-medium">In IVA</span>
                        </div>
                      )}
                      
                      <button
                        onClick={() => handleRemoveCreditor(index)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Save Button */}
        {creditors.length > 0 && (
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#10B981] hover:bg-[#059669] px-8"
              data-testid="save-creditors-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Saving...
                </span>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DebtsPage;
